/**
 * Raw Product Drawer Component
 *
 * Slide-out drawer for viewing and editing raw product details.
 */

import { useState, useCallback } from 'react';
import { Package, AlertTriangle } from 'lucide-react';
import { Drawer } from '@/components/ui';
import { Button } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Tabs, type Tab } from '@/components/ui';
import { InfosTab, TvaTab, NutritionTab, HistoryTab, PurchasePricesTab, PMPTab, StockTab } from './drawer';
import { useRawProduct, useUpdateRawProduct } from '../hooks/useRawProducts';
import { MarketType, MarketTypeLabels, type UpdateRawProductDto } from '@bstock/shared';
import { useBrandContext } from '@/hooks';

// Market type to Badge variant mapping
const MARKET_BADGE_VARIANTS: Record<MarketType, 'food' | 'nonfood' | 'maintenance'> = {
  [MarketType.FOOD]: 'food',
  [MarketType.FOOD_IND]: 'food',
  [MarketType.NON_FOOD]: 'nonfood',
  [MarketType.NON_FOOD_IND]: 'nonfood',
  [MarketType.MAINTENANCE]: 'maintenance',
};

interface RawProductDrawerProps {
  productId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit?: boolean;
  showFinancials?: boolean;
}

export function RawProductDrawer({ productId, open, onOpenChange, canEdit = true, showFinancials = false }: RawProductDrawerProps) {
  const [activeTab, setActiveTab] = useState('infos');
  const { isGroup } = useBrandContext();

  const { data: product, isLoading, error } = useRawProduct(productId);
  const updateMutation = useUpdateRawProduct();

  const handleFieldChange = useCallback(
    async (field: string, value: unknown) => {
      if (!productId) return;

      try {
        await updateMutation.mutateAsync({
          id: productId,
          data: { [field]: value } as UpdateRawProductDto,
        });
      } catch {
        // Error handled by mutation
      }
    },
    [productId, updateMutation]
  );

  const handleStatusToggle = useCallback(async () => {
    if (!productId || !product) return;

    try {
      await updateMutation.mutateAsync({
        id: productId,
        data: { isActive: !product.isActive },
      });
    } catch {
      // Error handled by mutation
    }
  }, [productId, product, updateMutation]);

  // Tabs configuration - Financial tabs only visible for managers+, Stock tab only for sites
  const tabs: Tab[] = [
    { id: 'infos', label: 'Infos' },
    ...(showFinancials ? [{ id: 'tva', label: 'TVA' }] : []),
    { id: 'nutrition', label: 'Allergenes' },
    // TODO: Historique tab disabled until implemented
    // { id: 'history', label: 'Historique' },
    ...(showFinancials ? [
      { id: 'purchase-prices', label: "Prix d'achat" },
      { id: 'pmp', label: 'PMP' },
    ] : []),
    ...(!isGroup ? [{ id: 'stock', label: 'Stock' }] : []),
  ];

  // Loading state
  if (!productId) return null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={isLoading ? 'Chargement...' : product?.name ?? 'Produit'}
      description={isLoading ? '' : product?.sku ?? ''}
      size="xlarge"
      footer={
        product && canEdit && (
          <div className="flex items-center justify-end w-full gap-3">
            <span className="text-sm text-neutral-500">
              {product.isActive ? 'Produit actif' : 'Produit inactif'}
            </span>
            <Button
              variant={product.isActive ? 'secondary' : 'primary'}
              onClick={handleStatusToggle}
              isLoading={updateMutation.isPending}
            >
              {product.isActive ? 'Desactiver' : 'Activer'}
            </Button>
          </div>
        )
      }
    >
      {isLoading && (
        <div className="space-y-4 animate-pulse">
          <div className="h-16 bg-neutral-100 rounded-xl" />
          <div className="h-10 bg-neutral-100 rounded-lg" />
          <div className="h-64 bg-neutral-100 rounded-xl" />
          <div className="h-32 bg-neutral-100 rounded-xl" />
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertTriangle className="w-12 h-12 text-danger-500 mb-4" />
          <p className="text-danger-500 font-medium">Erreur lors du chargement</p>
          <p className="text-sm text-neutral-500 mt-1">{error.message}</p>
        </div>
      )}

      {product && (
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-4">
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: product.category.color
                    ? `${product.category.color}20`
                    : 'rgb(var(--color-primary-100))',
                }}
              >
                <Package
                  className="w-8 h-8"
                  style={{
                    color: product.category.color || 'rgb(var(--color-primary-500))',
                  }}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold text-dark-500 truncate">
                      {product.name}
                    </h2>
                    <p className="text-sm text-neutral-500 mt-0.5">
                      {product.category.name} | SKU: {product.sku}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={MARKET_BADGE_VARIANTS[product.marketType]}>
                      {MarketTypeLabels[product.marketType]}
                    </Badge>
                    {product.isActive ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="neutral">Inactif</Badge>
                    )}
                  </div>
                </div>

                {product.allergens.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-warning-50 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
                    <span className="text-sm text-warning-600">
                      Allergenes: {product.allergens.join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Tab Content */}
          <div className="-mt-2">
            {activeTab === 'infos' && (
              <InfosTab product={product} onChange={handleFieldChange} />
            )}
            {activeTab === 'tva' && (
              <TvaTab product={product} onChange={handleFieldChange} />
            )}
            {activeTab === 'nutrition' && (
              <NutritionTab product={product} onChange={handleFieldChange} />
            )}
            {activeTab === 'history' && (
              <HistoryTab productId={product.id} />
            )}
            {activeTab === 'purchase-prices' && (
              <PurchasePricesTab
                productId={product.id}
                recipeUnitSymbol={product.recipeUnit.symbol}
              />
            )}
            {activeTab === 'pmp' && (
              <PMPTab
                productId={product.id}
                recipeUnitSymbol={product.recipeUnit.symbol}
              />
            )}
            {activeTab === 'stock' && (
              <StockTab productId={product.id} showFinancials={showFinancials} />
            )}
          </div>
        </div>
      )}

    </Drawer>
  );
}
